/**
 * CopilotAvatar — the copilot's face everywhere (launcher, header, empty
 * state, message rows, card buttons).
 *
 * Uses the robot clip the brand team supplied (`public/copilot-robot.mp4`,
 * a 192×192 muted 3 s loop ≈ a few hundred KB) inside the brand orb. Small
 * sizes (≤ 28 px) and reduced-motion users get the still poster frame
 * (`public/copilot-robot.png`) instead, so a long thread never mounts dozens
 * of <video> elements and nothing moves for people who asked it not to.
 */
import React, { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';

const BASE = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
export const COPILOT_VIDEO_SRC = `${BASE}/copilot-robot.mp4`;
export const COPILOT_POSTER_SRC = `${BASE}/copilot-robot.png`;
const VIDEO_MIN_SIZE = 32;

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(() =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
      : false
  );
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = (e) => setReduced(e.matches);
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else mq.removeListener(onChange);
    };
  }, []);
  return reduced;
}

/**
 * @param {number} size   pixel size of the orb (default 24)
 * @param {boolean} live  play the clip (false = poster only)
 * @param {string} className extra classes for the orb wrapper (e.g. cp-orb-ring, cp-think-ring)
 */
export default function CopilotAvatar({ size = 24, live = true, className, title = 'Copilot', ...rest }) {
  const reduced = usePrefersReducedMotion();
  const videoRef = useRef(null);
  const [failed, setFailed] = useState(false);
  const useVideo = live && !reduced && !failed && size >= VIDEO_MIN_SIZE;

  // Autoplay can be blocked until user interaction in some browsers; retry on the first click.
  useEffect(() => {
    if (!useVideo) return undefined;
    const el = videoRef.current;
    if (!el) return undefined;
    const tryPlay = () => {
      const p = el.play?.();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    };
    tryPlay();
    window.addEventListener('pointerdown', tryPlay, { once: true });
    return () => window.removeEventListener('pointerdown', tryPlay);
  }, [useVideo]);

  const media = useVideo ? (
    <video
      ref={videoRef}
      className="h-full w-full object-cover"
      src={COPILOT_VIDEO_SRC}
      poster={COPILOT_POSTER_SRC}
      autoPlay
      muted
      loop
      playsInline
      preload="metadata"
      aria-hidden="true"
      tabIndex={-1}
      onError={() => setFailed(true)}
      data-testid="copilot-avatar-video"
    />
  ) : (
    <img
      className="h-full w-full object-cover"
      src={COPILOT_POSTER_SRC}
      alt=""
      aria-hidden="true"
      draggable={false}
      onError={() => setFailed(true)}
      data-testid="copilot-avatar-poster"
    />
  );

  return (
    <span
      role="img"
      aria-label={title}
      className={cn('cp-orb cp-avatar overflow-hidden', className)}
      style={{ width: size, height: size }}
      data-size={size}
      {...rest}
    >
      {media}
    </span>
  );
}
